import { fetchDreamSiteBlogPosts } from 'actions/ContentActions'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fillArray } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import variables from 'styles/vendor/bootstrap/variables'
import ExperiencesPageBlogPostLoadingSkeleton from './ExperiencesPageBlogPostLoadingSkeleton'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import focusStyle from 'styles/utils/focusStyle'

const layoutContainerPadding = variables.gutterWidth / 2

const BlogPostCarousel = styled.div`
  display: grid;
  grid-template-columns: repeat(4, ${rem(270)});
  gap: ${rem(16)};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: ${rem(4)} ${rem(layoutContainerPadding)} ${rem(4)} ${rem(layoutContainerPadding)};
  margin: -${rem(4)} -${rem(layoutContainerPadding)} -${rem(4)} -${rem(layoutContainerPadding)};

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: initial;
    }
  }
`

const BlogImage = styled(Image)`
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
`

const BlogPostCard = styled(Clickable)`
  display: grid;
  grid-template-rows: ${rem(180)} 1fr min-content;
  gap: ${rem(12)};
  transition: background-color 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};

  &:focus-visible {
    ${focusStyle}
  }
`

const DREAM_SITE_EXPERIENCES_LOCATION = 'le-experiences'

interface Props {
  blogPosts?: Array<App.DreamSiteBlogPost>
}

function ExperiencesPageBlogPostsCarousel(props: Props) {
  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()
  const { blogPosts } = props
  const dispatch = useAppDispatch()

  const handleOnTileClick = useCallback(() => {
    trackEvent('ARTICLE_TILE_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  useEffect(() => {
    dispatch(fetchDreamSiteBlogPosts(DREAM_SITE_EXPERIENCES_LOCATION, 4))
    // eslint-disable-next-line
  }, [])

  if (!blogPosts?.length) {
    return null
  }

  return (<VerticalSpacer gap={32}>
    <Heading variant="heading3">
      {intl.formatMessage({
        id: 'Yn6asgGw6ALg8HNPwOnD',
        defaultMessage: 'Get inspired by our latest articles',
        description: 'Heading for blog posts carousel on experiences landing page',
      })}
    </Heading>
    <div>
      <BlogPostCarousel>
        {!blogPosts && fillArray(4).map((val) => <ExperiencesPageBlogPostLoadingSkeleton key={val} />)}
        {blogPosts?.map(post => <BlogPostCard key={post.id} href={post.url} target="_blank" onClick={handleOnTileClick}>
          <BlogImage image={post.image} height={180} fit="center" />
          <VerticalSpacer gap={4}>
            <Heading variant="heading6" lineClamp={2}>{post.title} </Heading>
            <BodyText variant="small" colour="neutral-two">{post.shortDescription}</BodyText>
          </VerticalSpacer>
          <TextLink size="medium">
            {intl.formatMessage({
              id: 'o15E919IBLX7ITFD46fP',
              defaultMessage: 'Read more',
              description: 'Link text to read full blog post article about experiences',
            })}
          </TextLink>
        </BlogPostCard>)}
      </BlogPostCarousel>
    </div>
  </VerticalSpacer>
  )
}

function mapStateToProps(state: App.State) {
  return {
    blogPosts: state.content.dreamSiteBlogPosts[DREAM_SITE_EXPERIENCES_LOCATION],
  }
}

export default connect(mapStateToProps)(ExperiencesPageBlogPostsCarousel)
